/**
 * SSML テンプレート入力欄で使えるプレースホルダ定義 (試聴条件パネル / 「？」ヘルプで使用)。
 *
 * 分割前は `components/BroadcastContentsEditor.tsx` の 7 か所に散在していたものを
 * 1 ファイルに集約した。トークン・説明文はすべて元のまま (文言変更なし)。
 *
 * ## `sample` フィールドについて (Issue #439)
 *
 * 各トークンには音声プレビューの「サンプル条件」パネルの初期値として使う
 * 代表値 (`sample`) を持たせている。`{delay_minutes_5}` などを未展開のまま
 * Polly に送ると「かっこ デレイ ミニッツ…」と読み上げられてしまうため、
 * **凡例と同じ場所でサンプル値も一元管理**する方針としている。
 *
 * サンプル値は「遅延 7 分 (= 420 秒) / 中央線 / 新宿駅 / 八王子方面」という
 * 1 つのシナリオで統一しているため、`{delay_minutes_1}` = 7 に対し
 * `{delay_minutes_5}` = 5 (5 分単位で切り捨て)、`{delay_minutes_10}` = 0
 * (10 分単位で切り捨て) と整合が取れている。
 *
 * ⚠️ 実際の SSML 展開は **サーバ側** (`lambda/common/preview_render.py` →
 * `ssml_builder`) が行う。ここを変えても本番の放送文面は変わらない
 * (プレビュー条件パネルの既定値と凡例の例示だけが変わる)。
 *
 * ## 秒単位トークンを凡例に載せない方針 (SBI-2)
 *
 * `{delay_seconds}` / `{delay}` (遅延秒数) は **凡例から撤去済み** で、
 * 新規のテンプレートでは選べないようにしている。理由は音声パターン数:
 * 秒単位で読み上げると「遅延秒数の種類 = 生成される音声ファイル数」になり、
 * 1 秒刻みでパターンが際限なく増える (実測で S3 の音声が 24 万オブジェクト /
 * 95GiB まで膨張していた)。分単位に載せると 1/10 程度に収まる。
 *
 * ⚠️ **サーバ側の展開ロジックは残っている**。既に保存済みのテンプレートに
 * `{delay_seconds}` が書かれていれば従来どおり展開・放送・試聴される
 * (`ssml_builder` の `_render` から外すと `KeyError` を拾って
 *  テンプレート生文字列が放送される縮退があるため触らない)。
 * つまりここでの撤去は「消す」ではなく **「新規に選べなくする」** が目的。
 *
 * なお秒トークンを外しても遅延の入力欄 (`resolveSampleFields` の `showDelay`) は
 * 分単位トークンで true になるため、保存済みの秒テンプレートも試聴できる。
 */

import type { ConnectingDelayJudgementMode } from '../../api/types';

import type { PlaceholderHelpItem, PlaceholderItem } from './types';

/**
 * 自列車遅延の「放送パターン (SSMLテンプレート)」入力欄で使えるプレースホルダ一覧。
 * 試聴時のプレビュー条件パネルの入力欄出し分けに使う。
 */
export const SELF_TRAIN_DELAY_PLACEHOLDERS: PlaceholderItem[] = [
  { token: '{delay_minutes_1}', description: '1分単位で切り捨てた遅延分数', sample: '7' },
  { token: '{delay_minutes_5}', description: '5分単位で切り捨てた遅延分数', sample: '5' },
  { token: '{delay_minutes_10}', description: '10分単位で切り捨てた遅延分数', sample: '0' },
  { token: '{delay_minutes}', description: '切り上げ・最低1分の遅延分数', sample: '7' },
  {
    token: '{route_name}',
    description: '自路線の一般的な線名 (未登録時は {route_id} の値)',
    sample: '中央線',
  },
  { token: '{route_id}', description: 'ATOS 線名コード (路線ID)', sample: '中央' },
  { token: '{route}', description: '{route_name} と同じ意味', sample: '中央線' },
];

/**
 * 接続線遅延 (transfer_aware モード) の「放送パターン (SSMLテンプレート)」入力欄で
 * 使えるプレースホルダ一覧。乗換駅名 (`{from_station}`) と方面 (`{direction_name}`) が
 * 自列車遅延と違って利用できる点が特徴。
 */
export const CONNECTING_TRANSFER_AWARE_PLACEHOLDERS: PlaceholderItem[] = [
  { token: '{delay_minutes_1}', description: '1分単位で切り捨てた遅延分数', sample: '7' },
  { token: '{delay_minutes_5}', description: '5分単位で切り捨てた遅延分数', sample: '5' },
  { token: '{delay_minutes_10}', description: '10分単位で切り捨てた遅延分数', sample: '0' },
  { token: '{from_station}', description: '乗換駅名', sample: '新宿' },
  {
    token: '{from_prefix}',
    description: '「<駅名>駅から」(乗換駅が無ければ空欄)',
    sample: '新宿駅から',
  },
  {
    token: '{direction_name}',
    description: '方面 (末尾に「方面行き」が自動付与)',
    sample: '八王子',
  },
  {
    token: '{route_name}',
    description: '接続線の一般的な線名 (未登録時は {route_id} の値)',
    sample: '中央線',
  },
  { token: '{route_id}', description: 'ATOS 線名コード (路線ID)', sample: '中央' },
];

/**
 * 発車見込み案内 (`departure_forecast`) で part1 / part2 に **共通** で使えるトークン (SBI-5)。
 *
 * `lambda/common/ssml_builder.py` の許可リスト
 * (`connecting_delay_departure_forecast` / `connecting_station_departure_forecast`) と一致させる。
 * **遅延分数 (`{delay_minutes_*}`) は読み上げない設計なので含めない**。
 *
 * `{departure_time}` は言語別に整形される (日本語は「12時13分」)。
 * `{headsign}` は接続便の行先 (末尾に「行」を補う)。取得できないときは
 * `{direction_name}` と同じ値 (「方面行き」は付与しない) になる。
 */
const DEPARTURE_FORECAST_COMMON_PLACEHOLDERS: PlaceholderItem[] = [
  {
    token: '{departure_time}',
    description: '発車見込み時刻 (例: 12時13分)',
    sample: '12時13分',
  },
  { token: '{departure_hour}', description: '発車見込み時刻の「時」(数字)', sample: '12' },
  { token: '{departure_minute}', description: '発車見込み時刻の「分」(数字)', sample: '13' },
  {
    token: '{headsign}',
    description: '接続便の行先 (末尾に「行」を補う。取得できないときは方面名)',
    sample: '八王子行',
  },
  {
    token: '{direction_name}',
    description: '{headsign} と同じ値 (行先 → 方面名の順で解決。「方面行き」は付かない)',
    sample: '八王子行',
  },
  {
    token: '{route_name}',
    description: '接続線の一般的な線名 (未登録時は {route_id} の値)',
    sample: '中央線',
  },
  { token: '{route_id}', description: 'ATOS 線名コード (路緝ID)', sample: '中央' },
  { token: '{route}', description: '{route_name} と同じ意味', sample: '中央線' },
];

/**
 * 接続線の発車見込み案内 (part1 / `departure_forecast`) の「放送パターン」入力欄で
 * 使えるプレースホルダ一覧。乗換駅名 (`{from_station}` / `{from_prefix}`) も使える。
 */
export const CONNECTING_DEPARTURE_FORECAST_PLACEHOLDERS: PlaceholderItem[] = [
  ...DEPARTURE_FORECAST_COMMON_PLACEHOLDERS,
  { token: '{from_station}', description: '乗換駅名', sample: '新宿' },
  {
    token: '{from_prefix}',
    description: '「<駅名>駅から」(乗換駅が無ければ空欄)',
    sample: '新宿駅から',
  },
];

/**
 * 接続線の発車見込み案内 (part2 / `departure_forecast`) の **方面部分文** で使える
 * プレースホルダ一覧。part2 の流儀に合わせ乗換駅は `{transfer_station}` (part1 の
 * `{from_station}` / `{from_prefix}` は持たない)。
 */
export const CONNECTING_STATION_DIRECTION_DEPARTURE_FORECAST_PLACEHOLDERS: PlaceholderItem[] = [
  ...DEPARTURE_FORECAST_COMMON_PLACEHOLDERS,
  { token: '{transfer_station}', description: '乗換駅名 (例: 赤羽)', sample: '赤羽' },
];

/**
 * 接続線遅延 (whole_line モード) の単一 SSML テンプレートで使えるプレースホルダ一覧。
 *
 * whole_line モードでは Lambda 側で乗換駅・方面が放送されないため、
 * `{from_prefix}` / `{from_station}` / `{direction_name}` は凡例から除外し、
 * 注釈 (note) として「空欄として扱われます」を凡例の下に明示する。
 */
export const CONNECTING_WHOLE_LINE_PLACEHOLDERS: PlaceholderItem[] = [
  { token: '{route_name}', description: '一般的な線名 (例: 京浜東北線)', sample: '中央線' },
  { token: '{route_id}', description: 'ATOS 線名コード (例: 京浜)', sample: '中央' },
  { token: '{route}', description: '{route_name} と同じ意味', sample: '中央線' },
  { token: '{delay_minutes}', description: '切り上げ・最低1分の遅延分数', sample: '7' },
];

/**
 * 接続線遅延 (section_scheduled モード) の単一 SSML テンプレートで使えるプレースホルダ一覧。
 * 乗換駅 (`{from_station}` / `{from_prefix}`) と方面 (`{direction_name}`) も
 * 利用可能 (Lambda 側で展開される)。
 */
export const CONNECTING_SECTION_SCHEDULED_PLACEHOLDERS: PlaceholderItem[] = [
  { token: '{route_name}', description: '一般的な線名 (例: 京浜東北線)', sample: '中央線' },
  { token: '{route_id}', description: 'ATOS 線名コード (例: 京浜)', sample: '中央' },
  { token: '{route}', description: '{route_name} と同じ意味', sample: '中央線' },
  { token: '{from_station}', description: '乗換駅名', sample: '新宿' },
  {
    token: '{from_prefix}',
    description: '「<駅名>駅からお」(乗換駅が無ければ空欄)',
    sample: '新宿駅から',
  },
  {
    token: '{direction_name}',
    description: '方面 (末尾に「方面行き」が自動付与)',
    sample: '八王子',
  },
  { token: '{delay_minutes}', description: '切り上げ・最低1分の遅延分数', sample: '7' },
];

// ---------------------------------------------------------------------------
// 接続線遅延 (乗換駅ベース / part2) 用のプレースホルダ凡例定数。
// ---------------------------------------------------------------------------

/**
 * 冒頭 SSML テンプレート (`ssml_intro_template`) で使えるプレースホルダ一覧。
 * 乗換駅名のみを扱うシンプルな凡例。
 */
export const CONNECTING_STATION_INTRO_PLACEHOLDERS: PlaceholderItem[] = [
  { token: '{transfer_station}', description: '乗換駅名 (例: 赤羽)', sample: '赤羽' },
];

/**
 * 接続線ブロック SSML テンプレート (`ssml_line_template`) で使えるプレースホルダ一覧。
 * 方面部分文を読点連結した文字列 `{directions_text}` を必ず含めることを想定する。
 */
export const CONNECTING_STATION_LINE_PLACEHOLDERS: PlaceholderItem[] = [
  { token: '{route_name}', description: '接続線の一般的な線名 (例: 埼京線)', sample: '中央線' },
  { token: '{route_id}', description: 'ATOS 線名コード (例: 埼京)', sample: '中央' },
  { token: '{route}', description: '{route_name} と同じ意味', sample: '中央線' },
  {
    token: '{directions_text}',
    description: '方面部分文を読点で連結した文字列 (方面ごとの遅延案内)',
    sample: '八王子方面は約5分遅れ',
  },
  { token: '{transfer_station}', description: '乗換駅名 (例: 赤羽)', sample: '赤羽' },
];

/**
 * 方面部分文 SSML テンプレート (`lines[j].delay_patterns[k].ssml_template`) で
 * 使えるプレースホルダ一覧。part1 の transfer_aware 用凡例に近いが、乗換駅
 * (`{from_station}` / `{from_prefix}`) は使わず、代わりに `{transfer_station}` を使う。
 */
export const CONNECTING_STATION_DIRECTION_PLACEHOLDERS: PlaceholderItem[] = [
  { token: '{direction_name}', description: '方面名 (例: 新宿)。必須項目', sample: '八王子' },
  { token: '{delay_minutes_1}', description: '1分単位で切り捨てた遅延分数', sample: '7' },
  { token: '{delay_minutes_5}', description: '5分単位で切り捨てた遅延分数', sample: '5' },
  { token: '{delay_minutes_10}', description: '10分単位で切り捨てた遅延分数', sample: '0' },
  { token: '{delay_minutes}', description: '切り上げ・最低1分の遅延分数', sample: '7' },
  { token: '{route_name}', description: '接続線の一般的な線名', sample: '中央線' },
  { token: '{route_id}', description: 'ATOS 線名コード', sample: '中央' },
  { token: '{transfer_station}', description: '乗換駅名', sample: '赤羽' },
];

/**
 * 方面部分文 SSML テンプレート (`lines[j].delay_patterns[k].ssml_template`) で
 * 使えるプレースホルダ一覧 — **判定モード `whole_line` (接続線全体の遅延) 用**。
 *
 * `whole_line` は接続線全体の最大遅延だけで判定する純化モードで、コンパネ上でも
 * 「方面」の入力を求めない (方面欄そのものを表示しない)。そのため
 * `{direction_name}` は展開しても空欄になるので **凡例から除外** する
 * (part1 の `CONNECTING_WHOLE_LINE_PLACEHOLDERS` と同じ考え方)。
 *
 * この凡例はプレビュー条件パネルの入力欄の出し分けにも使われるため
 * (`resolveSampleFields`)、whole_line では方面名の入力欄が現れない
 * = 本番でも読み上げられない項目を触らせない、という一貫した挙動になる。
 */
export const CONNECTING_STATION_DIRECTION_WHOLE_LINE_PLACEHOLDERS: PlaceholderItem[] = [
  { token: '{delay_minutes_1}', description: '1分単位で切り捨てた遅延分数', sample: '7' },
  { token: '{delay_minutes_5}', description: '5分単位で切り捨てた遅延分数', sample: '5' },
  { token: '{delay_minutes_10}', description: '10分単位で切り捨てた遅延分数', sample: '0' },
  { token: '{delay_minutes}', description: '切り上げ・最低1分の遅延分数', sample: '7' },
  { token: '{route_name}', description: '接続線の一般的な線名', sample: '中央線' },
  { token: '{route_id}', description: 'ATOS 線名コード', sample: '中央' },
  { token: '{transfer_station}', description: '乗換駅名', sample: '赤羽' },
];

/**
 * ヘルプ (「？」ボタン) に表示する **全コンテンツ種別・全判定モード共通** のプレースホルダ一覧。
 *
 * 以前は各 SSML 入力欄の直下に欄ごとの凡例を置いていたが、画面が長くなるため
 * 放送地点モーダル右上の「？」ボタンから 1 つの一覧として参照する方式に改めた。
 * 欄ごとに使えるトークンが違うため、`usage` に「どこで使えるか」を併記する。
 *
 * ⚠️ 秒単位トークン (`{delay_seconds}` / `{delay}`) は載せない (SBI-2 / `placeholders.test.ts`)。
 */
export const PLACEHOLDER_HELP_ITEMS: PlaceholderHelpItem[] = [
  {
    token: '{delay_minutes_1}',
    description: '1分単位で切り捨てた遅延分数',
    usage: '自列車遅延 / 接続線遅延 (乗り換え列車の遅延) / 方面部分文',
  },
  {
    token: '{delay_minutes_5}',
    description: '5分単位で切り捨てた遅延分数',
    usage: '自列車遅延 / 接続線遅延 (乗り換え列車の遅延) / 方面部分文',
  },
  {
    token: '{delay_minutes_10}',
    description: '10分単位で切り捨てた遅延分数',
    usage: '自列車遅延 / 接続線遅延 (乗り換え列車の遅延) / 方面部分文',
  },
  {
    token: '{delay_minutes}',
    description: '切り上げ・最低1分の遅延分数',
    usage: '自列車遅延 / 接続線遅延 (折り返し・接続線全体) / 方面部分文',
  },
  {
    token: '{route_name}',
    description: '線名 (自列車遅延は自路線、接続線遅延は接続線。未登録時は {route_id} の値)',
    usage: '全般',
  },
  { token: '{route_id}', description: 'ATOS 線名コード (路線ID)', usage: '全般' },
  {
    token: '{route}',
    description: '{route_name} と同じ意味',
    usage: '自列車遅延 / 接続線遅延 (折り返し・接続線全体) / 接続線ブロック',
  },
  {
    token: '{from_station}',
    description: '乗換駅名',
    usage: '接続線遅延 (乗り換え列車の遅延 / 折り返し列車の遅延 / 発車見込み案内)',
  },
  {
    token: '{from_prefix}',
    description: '「<駅名>駅から」(乗換駅が無ければ空欄)',
    usage: '接続線遅延 (乗り換え列車の遅延 / 折り返し列車の遅延 / 発車見込み案内)',
  },
  {
    token: '{direction_name}',
    description:
      '方面名 (接続線遅延では末尾に「方面行き」が自動付与。発車見込み案内では {headsign} と同じ値で「方面行き」は付かない)',
    usage: '接続線遅延 (乗り換え列車の遅延 / 折り返し列車の遅延 / 発車見込み案内) / 方面部分文',
  },
  {
    token: '{transfer_station}',
    description: '乗換駅名 (例: 赤羽)',
    usage: '乗換駅別の冒頭文 / 接続線ブロック / 方面部分文',
  },
  {
    token: '{directions_text}',
    description: '方面部分文を読点で連結した文字列 (方面ごとの遅延案内 / 発車見込み案内)',
    usage: '接続線ブロック',
  },
  {
    token: '{departure_time}',
    description: '発車見込み時刻 (例: 12時13分)',
    usage: '接続線の発車見込み案内',
  },
  {
    token: '{departure_hour}',
    description: '発車見込み時刻の「時」(数字)',
    usage: '接続線の発車見込み案内',
  },
  {
    token: '{departure_minute}',
    description: '発車見込み時刻の「分」(数字)',
    usage: '接続線の発車見込み案内',
  },
  {
    token: '{headsign}',
    description: '接続便の行先 (末尾に「行」を補う。取得できないときは方面名)',
    usage: '接続線の発車見込み案内',
  },
];

/**
 * 判定モードに対応する「方面部分文 SSML の凡例」を返す。
 *
 * `whole_line` (接続線全体の遅延) は方面を扱わないため `{direction_name}` を含まない
 * 凡例を返す。凡例は **プレビュー条件パネルの入力欄の出し分け** にも使われるので、
 * UI とプレビューで同じ規則を共有できるようここに集約する。
 */
export function resolveConnectingStationDirectionPlaceholders(
  mode: ConnectingDelayJudgementMode | undefined,
): PlaceholderItem[] {
  if (mode === 'departure_forecast') {
    return CONNECTING_STATION_DIRECTION_DEPARTURE_FORECAST_PLACEHOLDERS;
  }
  return mode === 'whole_line'
    ? CONNECTING_STATION_DIRECTION_WHOLE_LINE_PLACEHOLDERS
    : CONNECTING_STATION_DIRECTION_PLACEHOLDERS;
}
